<template>
    <div>
        <a-form :form="form" @submit="handleSubmit">
            <a-card :bordered="false" class="header-solid h-full" :bodyStyle="{ paddingTop: 0, paddingBottom: '40px' }">
                <template #title>
                    <h5 class="mb-0">User details</h5>
                </template>
                <a-row type="flex" :gutter="24">
                    <a-col :span="12" :lg="12" class="mb-12">
                        <a-row :gutter="[24]">
                            <a-col :span="24">
                                <a-form-item class="mb-10" label="Full Name" :colon="false">
                                    <a-input v-model="user.full_name" placeholder="Full Name" />
                                </a-form-item>
                            </a-col>
                            <a-col :span="24">
                                <a-form-item class="mb-10" label="Email" :colon="false">
                                    <a-input v-model="user.email" placeholder="Email Address" type="email" />
                                </a-form-item>
                            </a-col>
                            <a-col :span="24">
                                <a-form-item class="mb-10" label="Phone Number" :colon="false">
                                    <a-input v-model="user.phone_number" placeholder="Phone Number" />
                                </a-form-item>
                            </a-col>

                            <a-col :span="24" v-if="!isEdit">
                                <a-form-item class="mb-10" label="Password" :colon="false">
                                    <a-input v-model="user.password" placeholder="Password" type="password" />
                                </a-form-item>
                            </a-col>
                            <a-col :span="24">
                                <a-form-item class="mb-10" label="Company" :colon="false" :required="true">
                                    <a-select 
                                        v-model="user.company_id" 
                                        placeholder="Select Company (Required)"
                                        showSearch
                                        optionFilterProp="children"
                                    >
                                        <a-select-option 
                                            v-for="company in companies" 
                                            :key="company.id" 
                                            :value="company.id"
                                        >
                                            {{ company.name }}
                                        </a-select-option>
                                    </a-select>
                                </a-form-item>
                            </a-col>
                        </a-row>
                    </a-col>
                    <a-col :span="12" :lg="12" class="mb-12">
                        <a-row :gutter="[24]">
                            <a-col :span="24">
                                <a-card size="small" title="User Summary" style="margin-bottom: 16px;">
                                    <p><strong>Full Name:</strong> {{ user.full_name || 'Not set' }}</p>
                                    <p><strong>Email:</strong> {{ user.email || 'Not set' }}</p>
                                    <p><strong>Phone:</strong> {{ user.phone_number || 'Not set' }}</p>
                                    <p><strong>Company:</strong> {{ getSelectedCompanyName() }}</p>
                                    <p v-if="isEdit"><strong>ID:</strong> {{ user.id || 'Not set' }}</p>
                                    <p v-if="isEdit"><strong>Created At:</strong> {{ formatDateTime(user.created_at) }}</p>
                                </a-card>
                            </a-col>
                        </a-row>
                    </a-col>
                </a-row>
            </a-card>
            <a-button type="primary" html-type="submit" style="margin-top: 20px; margin-right: 8px;"> Submit </a-button>
            <a-button @click="handleCancel" style="margin-top: 20px;"> Cancel </a-button>
        </a-form>
    </div>
</template>

<script>
import router from "../../router/index.js";
import userAPI from "../../api/user.js";
import companyAPI from "../../api/company.js";
import moment from "moment";

export default {
    data() {
        return {
            user: {
                id: "",
                full_name: "",
                email: "",
                phone_number: "",
                password: "",
                company_id: '',
                created_at: null,
            },
            userEditData: null,
            companies: [],
            isEdit: false,
            form: this.$form.createForm(this, { name: "editUser" }),
        };
    },
    methods: {
        async getUserById(id) {
            return userAPI.getUserById(id);
        },
        async fetchCompanies() {
            try {
                const response = await companyAPI.getCompanies({ 
                    params: { 
                        page_size: 1000, // Get all companies for dropdown
                        page: 1 
                    } 
                });
                this.companies = response.data || [];
            } catch (error) {
                console.error('Error fetching companies:', error);
                this.companies = [];
            }
        },
        formatDateTime(dateTime) {
            if (!dateTime) return 'Not set';
            return moment(dateTime).format('YYYY-MM-DD HH:mm:ss');
        },
        getSelectedCompanyName() {
            if (!this.user.company_id) return 'No Company Selected';
            const company = this.companies.find(c => c.id === this.user.company_id);
            return company ? company.name : 'Unknown Company';
        },
        handleSubmit(e) {
            e.preventDefault();
            this.form.validateFields((err) => {
                if (!err) {
                    // Additional validation for company_id
                    if (!this.user.company_id) {
                        this.$message.error('Please select a company');
                        return;
                    }
                    
                    // Prepare user data for submission
                    const userData = {
                        ...this.user,
                    };

                    // Remove password if editing (password updates should be separate)
                    if (this.isEdit) {
                        delete userData.password;
                    }

                    // Request to create or update user
                    if (!this.isEdit) {
                        userAPI
                            .createUser(userData)
                            .then((responseData) => {
                                if (responseData.status == 201) {
                                    this.$message.success('User created successfully');
                                    router.push("/users/users-list");
                                }
                            })
                            .catch((error) => {
                                console.error('Error creating user:', error);
                            });
                    } else {
                        userAPI
                            .updateUser(userData)
                            .then((responseData) => {
                                if (responseData.status == 200) {
                                    this.$message.success('User updated successfully');
                                    router.push("/users/users-list");
                                }
                            })
                            .catch((error) => {
                                console.error('Error updating user:', error);
                            });
                    }
                }
            });
        },
        
        handleCancel() {
            router.push("/users/users-list");
        },
    },
    async mounted() {
        // Fetch companies for the dropdown
        await this.fetchCompanies();

        if (this.$route.params.id !== undefined) {
            this.userEditData = await this.getUserById(this.$route.params.id); // get user data according to ID
            this.isEdit = true;
            this.user.id = this.userEditData.id || '';
            this.user.full_name = this.userEditData.full_name || '';
            this.user.email = this.userEditData.email || '';
            this.user.phone_number = this.userEditData.phone_number || '';
            this.user.company_id = this.userEditData.company_id || '';
            this.user.created_at = this.userEditData.created_at || null;
        }
    },
};
</script>

<style lang="scss" scoped>
// User form specific styles
.ant-card {
    .ant-card-body {
        p {
            margin-bottom: 8px;
            
            strong {
                color: #1890ff;
            }
        }
    }
}
</style>
